import {
  ChoiceRow,
  ConfirmDialog,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  Radio,
  RadioGroup,
  ReadOnlyField,
  Select,
  Skeleton,
  formatCPF,
  formatDate,
  titleCase,
  type InputProps,
} from '@westhetics/ui'
import type { ReactNode } from 'react'
import { errorMessage } from '@/lib/errors'
import { useLookupPostalCode, type ClinicProfile, type ClinicSignatureMode } from './api'
import { DOC_TYPE_LABELS, formatDocNumber, onlyDigits } from './doc-number'
import { formatPostalCode, maskPhone, type ClinicFormState } from './form'
import { SIGNATURE_MODE_LABELS, STATES, TIMEZONES } from './labels'
import type { FormController } from './use-form-state'

/**
 * Os blocos do cadastro da clínica (docs/areas/administracao.md, "Empresa"), cada um com a linha
 * que diz onde o dado aparece. São os mesmos em Empresa, na implantação, na criação e no detalhe
 * do painel root. `readOnly` é a tela de quem lê sem editar (recepção e prestador, RN-ADM-09).
 */

type ClinicForm = FormController<ClinicFormState>

type StringKey<T> = { [K in keyof T]: T[K] extends string ? K : never }[keyof T]

/** Um campo de texto ligado ao formulário: o erro aparece ao sair do campo. */
export function TextField<T extends object>({
  form,
  name,
  label,
  optional,
  hint,
  readOnly,
  mask,
  className,
  ...input
}: {
  form: FormController<T>
  name: StringKey<T>
  label: string
  optional?: boolean
  hint?: ReactNode
  readOnly?: boolean
  /** A máscara aplicada a cada tecla (CEP, CPF, telefone). */
  mask?: (text: string) => string
  /** Classe do campo inteiro, como `col-span-full`. */
  className?: string
} & Omit<
  InputProps,
  'value' | 'onChange' | 'onBlur' | 'name' | 'form' | 'className' | 'readOnly'
>) {
  return (
    <Field
      label={label}
      optional={optional && !readOnly}
      error={form.errors[name]}
      hint={hint}
      className={className}
    >
      <Input
        autoComplete="off"
        {...input}
        readOnly={readOnly}
        value={form.values[name] as string}
        onChange={(event) => {
          const text = mask ? mask(event.target.value) : event.target.value
          form.update({ [name]: text } as unknown as Partial<T>)
        }}
        onBlur={() => form.touch(name)}
      />
    </Field>
  )
}

/** O esqueleto de um cadastro enquanto carrega: a forma dos blocos, nunca spinner. */
export function FormSkeleton({ blocks = 3 }: { blocks?: number }) {
  return (
    <div role="status" aria-label="Carregando" className="mt-6 flex flex-col gap-4">
      {Array.from({ length: blocks }, (_, index) => (
        <Skeleton key={index} className={index === 0 ? 'h-[170px] w-full' : 'h-[230px] w-full'} />
      ))}
    </div>
  )
}

/**
 * O documento da clínica: em leitura para quase todo mundo (é da Westhetics, RN-ADM-01); editável
 * pela gestora só na clínica de cadastro próprio, até concluir a implantação (RN-ADM-88).
 */
export function DocumentField({
  form,
  profile,
  readOnly,
}: {
  form: ClinicForm
  profile: Pick<ClinicProfile, 'docType' | 'docNumber' | 'isDocNumberEditable'>
  readOnly?: boolean
}) {
  if (profile.isDocNumberEditable && !readOnly) {
    return (
      <TextField
        form={form}
        name="docNumber"
        label="CNPJ ou CPF"
        inputMode="numeric"
        mask={(text) => formatDocNumber(text)}
        hint="O documento da clínica, como vai na qualificação das partes. Depois da implantação, só a Westhetics o altera."
      />
    )
  }
  return (
    <ReadOnlyField
      label={DOC_TYPE_LABELS[profile.docType]}
      value={formatDocNumber(profile.docNumber, profile.docType)}
    />
  )
}

export function IdentificationBlock({
  form,
  note,
  document,
  readOnly,
  children,
}: {
  form: ClinicForm
  note: string
  /** O campo do documento: não editável para a gestora, editável para root (RN-ADM-01). */
  document: ReactNode
  readOnly?: boolean
  /** Campos a mais no bloco, como os públicos em leitura. */
  children?: ReactNode
}) {
  return (
    <FormBlock title="Identificação" note={note}>
      <FieldGrid>
        {document}
        <TextField
          form={form}
          name="legalName"
          label="Razão social"
          maxLength={120}
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="tradeName"
          label="Nome fantasia"
          optional
          maxLength={80}
          readOnly={readOnly}
          hint="É o nome que a paciente vê no link, no e-mail e no WhatsApp. Vazio, vale a razão social."
        />
        {children}
      </FieldGrid>
    </FormBlock>
  )
}

/** Endereço com o CEP que preenche o resto pela API e deixa tudo editável (RN-ADM-03). */
export function AddressBlock({ form, readOnly }: { form: ClinicForm; readOnly?: boolean }) {
  const lookup = useLookupPostalCode()

  function changePostalCode(text: string) {
    const postalCode = formatPostalCode(text)
    const digits = onlyDigits(postalCode)
    const changed = digits !== onlyDigits(form.values.postalCode)
    form.update({ postalCode })
    if (!changed) return
    if (digits.length < 8) {
      lookup.reset()
      return
    }
    lookup.mutate(digits, {
      // O que a consulta não tem (CEP geral, sem rua nem bairro) fica como está.
      onSuccess: (address) =>
        form.update({
          city: address.city,
          state: address.state,
          ...(address.street ? { street: address.street } : {}),
          ...(address.district ? { district: address.district } : {}),
        }),
    })
  }

  // A consulta é um atalho: a falha vira dica no campo e o endereço segue à mão.
  const postalCodeHint = lookup.isPending
    ? 'Buscando o endereço…'
    : lookup.isError
      ? errorMessage(lookup.error)
      : undefined

  return (
    <FormBlock title="Endereço" note="Usado na qualificação da contratada.">
      <FieldGrid>
        <Field label="CEP" error={form.errors.postalCode} hint={postalCodeHint}>
          <Input
            inputMode="numeric"
            autoComplete="off"
            placeholder="00000-000"
            readOnly={readOnly}
            value={form.values.postalCode}
            onChange={(event) => changePostalCode(event.target.value)}
            onBlur={() => form.touch('postalCode')}
          />
        </Field>
        <TextField form={form} name="street" label="Rua" maxLength={120} readOnly={readOnly} />
        <TextField form={form} name="number" label="Número" maxLength={10} readOnly={readOnly} />
        <TextField
          form={form}
          name="complement"
          label="Complemento"
          optional
          maxLength={60}
          readOnly={readOnly}
        />
        <TextField form={form} name="district" label="Bairro" maxLength={80} readOnly={readOnly} />
        <TextField form={form} name="city" label="Cidade" maxLength={80} readOnly={readOnly} />
        <Field label="UF" error={form.errors.state}>
          <Select
            placeholder="Selecione"
            disabled={readOnly}
            value={form.values.state}
            onChange={(event) => form.update({ state: event.target.value })}
            onBlur={() => form.touch('state')}
          >
            {STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </Select>
        </Field>
      </FieldGrid>
    </FormBlock>
  )
}

export function ContactBlock({ form, readOnly }: { form: ClinicForm; readOnly?: boolean }) {
  return (
    <FormBlock title="Contato" note="O e-mail jurídico recebe a cópia de cada documento assinado.">
      <FieldGrid>
        <TextField
          form={form}
          name="legalEmail"
          label="E-mail jurídico"
          type="email"
          inputMode="email"
          autoCapitalize="none"
          placeholder="juridico@clinica.com.br"
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="phone"
          label="Telefone"
          optional
          inputMode="tel"
          placeholder="11 91234-5678"
          mask={maskPhone}
          readOnly={readOnly}
          hint="Aparece no rodapé do documento e na tela do paciente."
        />
        <Field
          label="Fuso horário"
          error={form.errors.timezone}
          hint="As datas dos documentos e os prazos são calculados nele."
        >
          <Select
            disabled={readOnly}
            value={form.values.timezone}
            onChange={(event) => form.update({ timezone: event.target.value })}
            onBlur={() => form.touch('timezone')}
          >
            {TIMEZONES.map((timezone) => (
              <option key={timezone.value} value={timezone.value}>
                {timezone.label}
              </option>
            ))}
          </Select>
        </Field>
      </FieldGrid>
    </FormBlock>
  )
}

export function LegalRepresentativeBlock({
  form,
  readOnly,
}: {
  form: ClinicForm
  readOnly?: boolean
}) {
  return (
    <FormBlock
      title={
        <>
          Responsável legal
          <span className="text-label-soft tracking-normal normal-case"> · opcional</span>
        </>
      }
      note={'Entra na qualificação das partes como "representada por".'}
    >
      <FieldGrid>
        <TextField
          form={form}
          name="representativeName"
          label="Nome"
          aria-label="Nome da responsável legal"
          maxLength={120}
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="representativeDocNumber"
          label="CPF"
          aria-label="CPF da responsável legal"
          inputMode="numeric"
          placeholder="000.000.000-00"
          mask={formatCPF}
          readOnly={readOnly}
        />
      </FieldGrid>
    </FormBlock>
  )
}

/** De 0 a 2 testemunhas, nome e CPF juntos; a ordem é a ordem impressa (RN-ADM-06). */
export function WitnessesBlock({ form, readOnly }: { form: ClinicForm; readOnly?: boolean }) {
  return (
    <FormBlock
      title="Testemunhas dos contratos"
      note="Assinam junto em todo documento emitido pela clínica."
    >
      <FieldGrid>
        <TextField
          form={form}
          name="witness1Name"
          label="Primeira testemunha"
          optional
          maxLength={120}
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="witness1DocNumber"
          label="CPF"
          aria-label="CPF da primeira testemunha"
          inputMode="numeric"
          placeholder="000.000.000-00"
          mask={formatCPF}
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="witness2Name"
          label="Segunda testemunha"
          optional
          maxLength={120}
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="witness2DocNumber"
          label="CPF"
          aria-label="CPF da segunda testemunha"
          inputMode="numeric"
          placeholder="000.000.000-00"
          mask={formatCPF}
          readOnly={readOnly}
        />
      </FieldGrid>
    </FormBlock>
  )
}

/** Quem assina pela clínica: a responsável, documento a documento, ou a assinatura automática (RN-ADM-07). */
export function ClinicSignatureBlock({
  form,
  readOnly,
  autoSignature,
}: {
  form: ClinicForm
  readOnly?: boolean
  /** Quem ligou a automática e quando, como está salvo. */
  autoSignature?: ClinicProfile['autoSignature']
}) {
  return (
    <FormBlock
      title="Assinatura da clínica"
      note="Quem assina pela clínica em cada documento enviado."
    >
      <RadioGroup
        aria-label="Assinatura da clínica"
        disabled={readOnly}
        value={form.values.clinicSignatureMode}
        onValueChange={(value) =>
          form.update({ clinicSignatureMode: value as ClinicSignatureMode })
        }
      >
        <ChoiceRow
          control={<Radio value="manual" />}
          description="Cada documento enviado espera a assinatura da clínica, feita por quem está logado, em Assinaturas."
        >
          {SIGNATURE_MODE_LABELS.manual}
        </ChoiceRow>
        <ChoiceRow
          control={<Radio value="automatic" />}
          description="Todo documento enviado sai assinado pela clínica em nome de quem ativou, com data, hora e hash, sem revisão."
        >
          {SIGNATURE_MODE_LABELS.automatic}
        </ChoiceRow>
      </RadioGroup>
      {autoSignature && form.values.clinicSignatureMode === 'automatic' ? (
        <p className="font-body text-eyebrow text-label mt-3 leading-[1.5] font-light tracking-normal">
          Ativada por {titleCase(autoSignature.enabledBy.name)} em{' '}
          {formatDate(new Date(autoSignature.enabledAt))}.
        </p>
      ) : null}
    </FormBlock>
  )
}

/** A confirmação de RN-ADM-07, antes de salvar a assinatura automática. */
export function AutoSignatureDialog({
  open,
  onOpenChange,
  onConfirm,
  loading,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  loading?: boolean
}) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Ativar assinatura automática?"
      effect="Todo documento enviado a partir de agora sai assinado pela clínica em seu nome, sem revisão. Documentos já enviados não mudam. Alternativa: assinar cada documento em Assinaturas."
      confirmLabel="Ativar"
      loading={loading}
      loadingLabel="Ativando…"
      onConfirm={onConfirm}
    />
  )
}
